import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth, hasOAuthCallback } from './components/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute';
import { homeForProfile, INTERNAL_ROLES } from './lib/refonteNav';

// Redirige vers l'accueil V2 du profil courant (index de /app).
const AppHome: React.FC = () => {
  const { profile, isLoading } = useAuth();
  // Ne pas router tant que la session se résout / qu'un callback OAuth est en cours.
  if (isLoading || hasOAuthCallback()) return null;
  return <Navigate to={homeForProfile(profile)} replace />;
};

// Layout
import Layout from './components/Layout';

// Pages
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import AppointmentsPage from './pages/AppointmentsPage';
import AppointmentDetailPage from './pages/AppointmentDetailPage';
import ProspectingPage from './pages/ProspectingPage';
import ProspectingDayDetailPage from './pages/ProspectingDayDetailPage';
import AgendaPlanningPage from './pages/AgendaPlanningPage';
import UsersPage from './pages/UsersPage';
import LoginPage from './pages/LoginPage';
import ConfirmationRDVsPage from './pages/ConfirmationRDVsPageNew';
import CampagnesOverviewPage from './pages/CampagnesOverviewPage';
import CampagneDetailPage from './pages/CampagneDetailPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import StatisticsPage from './pages/StatisticsPage';
import ClientDetailNewPage from './pages/ClientDetailNewPage';
import StatsManagerPage from './pages/StatsManagerPage';

// Refonte (nouveau shell + vues)
import RefonteShell from './components/refonte/RefonteShell';
import SdrDayPage from './pages/refonte/SdrDayPage';
import CsmHealthPage from './pages/refonte/CsmHealthPage';
import KanbanPage from './pages/refonte/KanbanPage';
import ClientPortalPage from './pages/refonte/ClientPortalPage';
import PortalRegistrePage from './pages/refonte/PortalRegistrePage';
import PortalRdvPage from './pages/refonte/PortalRdvPage';
import PortalJourneesPage from './pages/refonte/PortalJourneesPage';
import RdvAStatuerPage from './pages/refonte/RdvAStatuerPage';
import RdvAValiderPage from './pages/refonte/RdvAValiderPage';
import OnboardingPage from './pages/refonte/OnboardingPage';
import AgendaPage from './pages/refonte/AgendaPage';
import TeamManagementPage from './pages/refonte/TeamManagementPage';
import ClientUsersPage from './pages/refonte/ClientUsersPage';
import RdvSearchPage from './pages/refonte/RdvSearchPage';
import CallsPage from './pages/refonte/CallsPage';
import CampagnesPage from './pages/refonte/CampagnesPage';
import PortalCampagnesPage from './pages/refonte/PortalCampagnesPage';
import DataToCompletePage from './pages/refonte/DataToCompletePage';
import RdvConfirmerPage from './pages/refonte/RdvConfirmerPage';
import ProductionSdrPage from './pages/refonte/ProductionSdrPage';
import StatsEquipePage from './pages/refonte/StatsEquipePage';
import DemandeListePage from './pages/refonte/DemandeListePage';
import CalibrageGuidePage from './pages/refonte/CalibrageGuidePage';
import ClientsRegistryPage from './pages/refonte/ClientsRegistryPage';
import ClientDossierPage from './pages/refonte/ClientDossierPage';
import ClientPerformancePage from './pages/refonte/ClientPerformancePage';
import DirectionBpPage from './pages/refonte/DirectionBpPage';
import DayToDayPage from './pages/refonte/DayToDayPage';
import FacturationPage from './pages/refonte/FacturationPage';
import CalibragesPage from './pages/refonte/CalibragesPage';
import KitPage from './pages/refonte/KitPage';

// Auth Pages
import ConfirmPage from './pages/auth/ConfirmPage';
import InvitePage from './pages/auth/InvitePage';
import MagicPage from './pages/auth/MagicPage';
import ChangeEmailPage from './pages/auth/ChangeEmailPage';
import ResetPage from './pages/auth/ResetPage';
import ReauthenticatePage from './pages/auth/ReauthenticatePage';
import FirstLoginPage from './pages/auth/FirstLoginPage';
import SetupPage from './pages/auth/SetupPage';
import AidePage from './pages/refonte/AidePage';

function App() {
  return (
    <LanguageProvider>
      <div className="min-h-screen bg-gray-50">
        <Router>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/code" element={<FirstLoginPage />} />
              <Route path="/auth/setup" element={<SetupPage />} />
              
              {/* Auth Routes */}
              <Route path="/auth/confirm" element={<ConfirmPage />} />
              <Route path="/auth/invite" element={<InvitePage />} />
              <Route path="/auth/magic" element={<MagicPage />} />
              <Route path="/auth/change-email" element={<ChangeEmailPage />} />
              <Route path="/auth/reset" element={<ResetPage />} />
              <Route path="/auth/reauthenticate" element={<ReauthenticatePage />} />
              
              {/* Catch-all route for debugging */}
              <Route path="/debug" element={
                <div className="p-8">
                  <h1>Debug Info</h1>
                  <p>Supabase URL: {import.meta.env.VITE_SUPABASE_URL}</p>
                  <p>Environment: {import.meta.env.MODE}</p>
                </div>
              } />
              
              {/* Racine → V2 (porte d'entrée) */}
              <Route path="/" element={<Navigate to="/app" replace />} />

              {/* V2 — nouveau shell, porte d'entrée */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <RefonteShell />
                </ProtectedRoute>
              }>
                <Route index element={<AppHome />} />
                <Route path="portail" element={<ProtectedRoute allow={['client', 'super_admin']}><ClientPortalPage /></ProtectedRoute>} />
                <Route path="portail/registre" element={<ProtectedRoute allow={['client', ...INTERNAL_ROLES]}><PortalRegistrePage /></ProtectedRoute>} />
                <Route path="portail/rdv/:id" element={<ProtectedRoute allow={['client', ...INTERNAL_ROLES]}><PortalRdvPage /></ProtectedRoute>} />
                <Route path="portail/journees" element={<ProtectedRoute allow={['client', 'super_admin']}><PortalJourneesPage /></ProtectedRoute>} />
                <Route path="portail/campagnes" element={<ProtectedRoute allow={['client', ...INTERNAL_ROLES]}><PortalCampagnesPage /></ProtectedRoute>} />
                <Route path="portail/performance" element={<ProtectedRoute allow={['client', ...INTERNAL_ROLES]}><ClientPerformancePage /></ProtectedRoute>} />
                <Route path="ma-journee" element={<ProtectedRoute allow={INTERNAL_ROLES}><SdrDayPage /></ProtectedRoute>} />
                <Route path="rdv-a-statuer" element={<ProtectedRoute allow={INTERNAL_ROLES}><RdvAStatuerPage /></ProtectedRoute>} />
                <Route path="rdv-a-valider" element={<ProtectedRoute allow={['csm', 'admin', 'super_admin']}><RdvAValiderPage /></ProtectedRoute>} />
                <Route path="rdv" element={<ProtectedRoute allow={INTERNAL_ROLES}><RdvSearchPage /></ProtectedRoute>} />
                <Route path="sante" element={<ProtectedRoute allow={INTERNAL_ROLES}><CsmHealthPage /></ProtectedRoute>} />
                <Route path="kanban" element={<ProtectedRoute allow={INTERNAL_ROLES}><KanbanPage /></ProtectedRoute>} />
                <Route path="bp" element={<ProtectedRoute allow={INTERNAL_ROLES}><DirectionBpPage /></ProtectedRoute>} />
                <Route path="day-to-day" element={<ProtectedRoute allow={INTERNAL_ROLES}><DayToDayPage /></ProtectedRoute>} />
                <Route path="rdv-confirmer" element={<ProtectedRoute allow={INTERNAL_ROLES}><RdvConfirmerPage /></ProtectedRoute>} />
                <Route path="production" element={<ProtectedRoute allow={INTERNAL_ROLES}><ProductionSdrPage /></ProtectedRoute>} />
                <Route path="stats-equipe" element={<ProtectedRoute allow={INTERNAL_ROLES}><StatsEquipePage /></ProtectedRoute>} />
                <Route path="demande-liste" element={<ProtectedRoute allow={INTERNAL_ROLES}><DemandeListePage /></ProtectedRoute>} />
                <Route path="facturation" element={<ProtectedRoute allow={INTERNAL_ROLES}><FacturationPage /></ProtectedRoute>} />
                <Route path="calibrages" element={<ProtectedRoute allow={INTERNAL_ROLES}><CalibragesPage /></ProtectedRoute>} />
                <Route path="onboarding" element={<ProtectedRoute allow={INTERNAL_ROLES}><OnboardingPage /></ProtectedRoute>} />
                <Route path="calibrage-guide" element={<ProtectedRoute allow={INTERNAL_ROLES}><CalibrageGuidePage /></ProtectedRoute>} />
                <Route path="clients" element={<ProtectedRoute allow={INTERNAL_ROLES}><ClientsRegistryPage /></ProtectedRoute>} />
                <Route path="clients/:id" element={<ProtectedRoute allow={INTERNAL_ROLES}><ClientDossierPage /></ProtectedRoute>} />
                <Route path="agenda" element={<ProtectedRoute allow={INTERNAL_ROLES}><AgendaPage /></ProtectedRoute>} />
                <Route path="equipe" element={<ProtectedRoute allow={INTERNAL_ROLES}><TeamManagementPage /></ProtectedRoute>} />
                <Route path="utilisateurs-clients" element={<ProtectedRoute allow={['admin', 'super_admin']}><ClientUsersPage /></ProtectedRoute>} />
                <Route path="echanges" element={<ProtectedRoute allow={INTERNAL_ROLES}><CallsPage /></ProtectedRoute>} />
                <Route path="campagnes" element={<ProtectedRoute allow={INTERNAL_ROLES}><CampagnesPage /></ProtectedRoute>} />
                <Route path="donnees" element={<ProtectedRoute allow={INTERNAL_ROLES}><DataToCompletePage /></ProtectedRoute>} />
                <Route path="aide" element={<ProtectedRoute allow={INTERNAL_ROLES}><AidePage /></ProtectedRoute>} />
                <Route path="_kit" element={<KitPage />} />
              </Route>

              {/* Ancienne app — parquée sous /legacy (filet, dé-priorisée) */}
              <Route path="/legacy" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<ClientDashboardPage />} />
                <Route path="old-dashboard" element={<DashboardPage />} />
                <Route path="appointments" element={<AppointmentsPage />} />
                <Route path="appointments/:id" element={<AppointmentDetailPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:clientId" element={<ClientDetailNewPage />} />
                <Route path="agenda-planning" element={<AgendaPlanningPage />} />
                <Route path="confirmation-rdvs" element={<ConfirmationRDVsPage />} />
                <Route path="campagnes" element={<CampagnesOverviewPage />} />
                <Route path="campagnes/:client_id" element={<CampagneDetailPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="prospecting" element={<ProspectingPage />} />
                <Route path="prospecting/:id" element={<ProspectingDayDetailPage />} />
                <Route path="statistiques" element={<StatisticsPage />} />
                <Route path="stats-manager" element={<StatsManagerPage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </Router>
      </div>
    </LanguageProvider>
  );
}

export default App